import React from 'react';
import uuid from 'uuid-random';
import {
  StyledTextType,
  TextTypesWrapper,
  ExemplarWrapper,
  ExemplarHeaderWrapper,
  FlexWrapper,
  StyledTextTypeLabel,
} from '../Styled';
import { ExemplarType, TextType, TextTypes } from '../../types';
import { FIRST_EXEMPLAR_ROUTE, TEXT_PRESENTATION } from '../../constants/navigation';
import { COLORS } from '../../constants';
import { getCollectionIcon, getStageColors } from '../../utils';
import { BookLabelName } from '../../types/exemplars';
import { EngagementPrompts } from '../EngagementPrompts';
import { ColoredTooltip } from '../Tooltip/ColoredTooltip';
import { Flex, FlexBehavior, FlexJustify } from 'react-magma-dom';
import { exemplarColoredTooltipStyles } from '../../constants/tooltip';

interface IExemplar {
  exemplar: ExemplarType;
}

interface ITextTypeLabel {
  url: string;
  hasTooltip?: boolean;
  children: JSX.Element;
}

const ALLOWED_LATER_ADDITIONAL_TEXT_TYPE_LENGTH = 1;

export const Exemplar: React.FC<IExemplar> = (props): JSX.Element => {
  const { exemplar } = props;

  const { title, textTypes, engagement } = exemplar;

  const [stage, level] = title.split(' ');
  const { main: mainColor } = getStageColors(stage.toLowerCase());
  const TextTypeLabel = (props: ITextTypeLabel): JSX.Element => {
    const { url, hasTooltip, children } = props;

    return (
      <StyledTextType
        key={uuid()}
        hasTooltip={hasTooltip}
        hoverFontColor={COLORS.seaBlue}
        color={COLORS.seaBlue}
        to={url}
      >
        {children}
      </StyledTextType>
    );
  };

  const renderTypes = (types: TextType[]): JSX.Element[] => {
    return types.map((type: TextType) => {
      const { exemplarId, title, additionalTextTypes } = type;
      const nextUrl = FIRST_EXEMPLAR_ROUTE[stage.toLowerCase()] || TEXT_PRESENTATION;

      return (
        <>
          {title === TextTypes.hybrid && additionalTextTypes ? (
            <TextTypeLabel key={uuid()} url={`exemplar/${exemplarId}/default/${nextUrl}`} hasTooltip>
              <Flex behavior={FlexBehavior.container} justify={FlexJustify.center}>
                <ColoredTooltip
                  containerStyle={exemplarColoredTooltipStyles}
                  background={mainColor}
                  content={additionalTextTypes.join(' / ')}
                >
                  <StyledTextTypeLabel data-testid="hybrid-label">{title}</StyledTextTypeLabel>
                </ColoredTooltip>
              </Flex>
            </TextTypeLabel>
          ) : (
            <TextTypeLabel key={uuid()} url={`exemplar/${exemplarId}/default/${nextUrl}`}>
              <StyledTextTypeLabel data-testid="text-type-label">
                {additionalTextTypes?.length === ALLOWED_LATER_ADDITIONAL_TEXT_TYPE_LENGTH
                  ? `${title} \u2013 ${additionalTextTypes.join('')}`
                  : title}
              </StyledTextTypeLabel>
            </TextTypeLabel>
          )}
        </>
      );
    });
  };

  // If there is only one button per line, then it should occupy the entire width of the line
  const engagementPromptsFullWidth = textTypes.length % 3 === 0;

  return (
    <ExemplarWrapper margin="40px auto 41px">
      <ExemplarHeaderWrapper letterColor={COLORS[level]} backgroundColor={COLORS[`${level}BG`]}>
        <FlexWrapper>
          <span style={{ padding: '0 12px 0px 0px' }}>{stage}</span>
          {getCollectionIcon(`${stage} ${level}` as BookLabelName)}
        </FlexWrapper>
      </ExemplarHeaderWrapper>
      <TextTypesWrapper>
        {renderTypes(textTypes)}
        {engagement && (
          <EngagementPrompts
            engagement={engagement}
            stage={stage}
            level={level}
            isFullWidth={engagementPromptsFullWidth}
          />
        )}
      </TextTypesWrapper>
    </ExemplarWrapper>
  );
};
